import React, { useEffect, useState } from "react";
import { supabase } from "../supabase";
import { withRetry } from "../utils/supabaseErrorHandler";

const formatPrice = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(3);
};

const parsePrice = (value) => {
  const price = Number(String(value).replace(",", "."));
  if (Number.isNaN(price) || price < 0) return null;
  return price;
};

const TripPricingSettings = ({ open, onClose, onCatalogChanged }) => {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [prices, setPrices] = useState([]);
  const [newProductName, setNewProductName] = useState("");
  const [newProductPrixUsine, setNewProductPrixUsine] = useState("");
  const [productPrixDrafts, setProductPrixDrafts] = useState({});
  const [newDestinationName, setNewDestinationName] = useState("");
  const [selectedDestinationId, setSelectedDestinationId] = useState("");
  const [priceDrafts, setPriceDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");

  const loadCatalog = async () => {
    setLoading(true);
    setError(null);
    try {
      const [productsRes, destinationsRes, pricesRes] = await Promise.all([
        withRetry(() =>
          supabase.from("schedule_products").select("*").order("name", { ascending: true })
        ),
        withRetry(() =>
          supabase.from("schedule_destinations").select("*").order("name", { ascending: true })
        ),
        withRetry(() =>
          supabase.from("schedule_destination_prices").select("*")
        ),
      ]);

      if (productsRes.error) throw productsRes.error;
      if (destinationsRes.error) throw destinationsRes.error;
      if (pricesRes.error) throw pricesRes.error;

      const loadedProducts = productsRes.data || [];
      setProducts(loadedProducts);
      setDestinations(destinationsRes.data || []);
      setPrices(pricesRes.data || []);

      const drafts = {};
      loadedProducts.forEach((product) => {
        drafts[product.id] = formatPrice(product.prix_usine ?? 0);
      });
      setProductPrixDrafts(drafts);

      if (!selectedDestinationId && destinationsRes.data?.length) {
        setSelectedDestinationId(destinationsRes.data[0].id);
      }
    } catch (err) {
      console.error("Error loading pricing catalog:", err);
      setError(
        err?.message?.includes("schema cache") || err?.code === "42P01"
          ? "Tables manquantes. Exécutez la migration SQL trip_destination_pricing dans Supabase."
          : err?.message?.includes("prix_usine")
          ? "Colonne prix_usine manquante. Exécutez supabase/trip_prix_usine.sql dans Supabase."
          : "Impossible de charger le catalogue tarifs."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadCatalog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!selectedDestinationId) {
      setPriceDrafts({});
      return;
    }
    const drafts = {};
    products.forEach((product) => {
      const row = prices.find(
        (p) => p.destination_id === selectedDestinationId && p.product_id === product.id
      );
      drafts[product.id] = row ? formatPrice(row.price) : "";
    });
    setPriceDrafts(drafts);
  }, [selectedDestinationId, products, prices]);

  const notifyParent = () => {
    if (onCatalogChanged) onCatalogChanged();
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    const name = newProductName.trim();
    if (!name) {
      setError("Indiquez un nom de produit.");
      return;
    }
    const prixUsine = parsePrice(newProductPrixUsine === "" ? "0" : newProductPrixUsine);
    if (prixUsine === null) {
      setError("Prix usine invalide.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error: insertError } = await withRetry(() =>
        supabase.from("schedule_products").insert([{ name, prix_usine: prixUsine }])
      );
      if (insertError) throw insertError;
      setNewProductName("");
      setNewProductPrixUsine("");
      setSuccess("Produit ajouté.");
      setTimeout(() => setSuccess(""), 2500);
      await loadCatalog();
      notifyParent();
    } catch (err) {
      console.error(err);
      setError(
        err?.message?.includes("duplicate")
          ? "Ce produit existe déjà."
          : err?.message?.includes("prix_usine")
          ? "Colonne prix_usine manquante. Exécutez supabase/trip_prix_usine.sql dans Supabase."
          : "Erreur lors de l'ajout du produit."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProductPrixUsine = async (productId) => {
    const prixUsine = parsePrice(productPrixDrafts[productId]);
    if (prixUsine === null) {
      setError("Prix usine invalide.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error: updateError } = await withRetry(() =>
        supabase
          .from("schedule_products")
          .update({ prix_usine: prixUsine })
          .eq("id", productId)
      );
      if (updateError) throw updateError;
      setSuccess("Prix usine enregistré.");
      setTimeout(() => setSuccess(""), 2500);
      await loadCatalog();
      notifyParent();
    } catch (err) {
      console.error(err);
      setError(
        err?.message?.includes("prix_usine")
          ? "Colonne prix_usine manquante. Exécutez supabase/trip_prix_usine.sql dans Supabase."
          : "Erreur lors de l'enregistrement du prix usine."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Supprimer ce produit et ses tarifs ?")) return;
    setSaving(true);
    setError(null);
    try {
      const { error: deleteError } = await withRetry(() =>
        supabase.from("schedule_products").delete().eq("id", productId)
      );
      if (deleteError) throw deleteError;
      await loadCatalog();
      notifyParent();
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la suppression du produit.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddDestination = async (e) => {
    e.preventDefault();
    const name = newDestinationName.trim();
    if (!name) {
      setError("Indiquez une destination.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data, error: insertError } = await withRetry(() =>
        supabase.from("schedule_destinations").insert([{ name }]).select()
      );
      if (insertError) throw insertError;
      setNewDestinationName("");
      setSelectedDestinationId(data[0].id);
      setSuccess("Destination ajoutée.");
      setTimeout(() => setSuccess(""), 2500);
      await loadCatalog();
      notifyParent();
    } catch (err) {
      console.error(err);
      setError(err?.message?.includes("duplicate") ? "Cette destination existe déjà." : "Erreur lors de l'ajout de la destination.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDestination = async (destinationId) => {
    if (!window.confirm("Supprimer cette destination et ses tarifs ?")) return;
    setSaving(true);
    setError(null);
    try {
      const { error: deleteError } = await withRetry(() =>
        supabase.from("schedule_destinations").delete().eq("id", destinationId)
      );
      if (deleteError) throw deleteError;
      if (selectedDestinationId === destinationId) {
        setSelectedDestinationId("");
      }
      await loadCatalog();
      notifyParent();
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la suppression de la destination.");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrices = async () => {
    if (!selectedDestinationId) {
      setError("Sélectionnez une destination.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const upserts = [];
      const deletes = [];

      products.forEach((product) => {
        const raw = (priceDrafts[product.id] || "").trim();
        const existing = prices.find(
          (p) => p.destination_id === selectedDestinationId && p.product_id === product.id
        );
        if (raw === "") {
          if (existing) deletes.push(existing.id);
          return;
        }
        const price = parsePrice(raw);
        if (price === null) {
          throw new Error(`Prix invalide pour ${product.name}`);
        }
        upserts.push({
          destination_id: selectedDestinationId,
          product_id: product.id,
          price,
        });
      });

      if (deletes.length > 0) {
        const { error: deleteError } = await withRetry(() =>
          supabase.from("schedule_destination_prices").delete().in("id", deletes)
        );
        if (deleteError) throw deleteError;
      }

      if (upserts.length > 0) {
        const { error: upsertError } = await withRetry(() =>
          supabase
            .from("schedule_destination_prices")
            .upsert(upserts, { onConflict: "destination_id,product_id" })
        );
        if (upsertError) throw upsertError;
      }

      setSuccess("Tarifs enregistrés.");
      setTimeout(() => setSuccess(""), 2500);
      await loadCatalog();
      notifyParent();
    } catch (err) {
      console.error(err);
      setError(err.message || "Erreur lors de l'enregistrement des tarifs.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content trip-pricing-settings-modal">
        <button className="modal-close" onClick={onClose} type="button">
          X
        </button>
        <section className="trip-scheduler-form-section">
          <h2>Tarifs destinations</h2>
          <p className="trip-pricing-settings-hint">
            1) Produits + prix usine (achat). 2) Destinations + prix de vente.
          </p>

          <div className="trip-pricing-tabs">
            <button
              type="button"
              className={`trip-pricing-tab ${tab === "products" ? "active" : ""}`}
              onClick={() => setTab("products")}
            >
              1. Produits
            </button>
            <button
              type="button"
              className={`trip-pricing-tab ${tab === "destinations" ? "active" : ""}`}
              onClick={() => setTab("destinations")}
            >
              2. Destinations & prix
            </button>
          </div>

          {success && <div className="trip-scheduler-success-message">{success}</div>}
          {error && <div className="trip-scheduler-error-message">{error}</div>}
          {loading && <p>Chargement...</p>}

          {!loading && tab === "products" && (
            <div className="trip-pricing-panel">
              <form onSubmit={handleAddProduct} className="trip-pricing-product-form">
                <input
                  type="text"
                  value={newProductName}
                  onChange={(e) => setNewProductName(e.target.value)}
                  placeholder="Produit (ex: h16)"
                  disabled={saving}
                />
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={newProductPrixUsine}
                  onChange={(e) => setNewProductPrixUsine(e.target.value)}
                  placeholder="Prix usine"
                  disabled={saving}
                />
                <button type="submit" className="trip-scheduler-save-btn" disabled={saving}>
                  Ajouter
                </button>
              </form>
              <ul className="trip-pricing-list">
                {products.length === 0 && (
                  <li className="trip-pricing-empty">Aucun produit pour le moment.</li>
                )}
                {products.map((product) => (
                  <li key={product.id} className="trip-pricing-product-row">
                    <span className="trip-pricing-product-name">{product.name}</span>
                    <label className="trip-pricing-usine-label">
                      Prix usine
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={productPrixDrafts[product.id] ?? ""}
                        onChange={(e) =>
                          setProductPrixDrafts((prev) => ({
                            ...prev,
                            [product.id]: e.target.value,
                          }))
                        }
                        disabled={saving}
                      />
                    </label>
                    <button
                      type="button"
                      className="trip-scheduler-save-btn"
                      onClick={() => handleSaveProductPrixUsine(product.id)}
                      disabled={saving}
                    >
                      Sauver
                    </button>
                    <button
                      type="button"
                      className="trip-scheduler-cancel-btn"
                      onClick={() => handleDeleteProduct(product.id)}
                      disabled={saving}
                    >
                      Supprimer
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!loading && tab === "destinations" && (
            <div className="trip-pricing-panel">
              <form onSubmit={handleAddDestination} className="trip-pricing-inline-form">
                <input
                  type="text"
                  value={newDestinationName}
                  onChange={(e) => setNewDestinationName(e.target.value)}
                  placeholder="ex: mahres"
                  disabled={saving}
                />
                <button type="submit" className="trip-scheduler-save-btn" disabled={saving}>
                  Ajouter
                </button>
              </form>

              {destinations.length === 0 ? (
                <p className="trip-pricing-empty">Aucune destination. Ajoutez-en une ci-dessus.</p>
              ) : (
                <>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="pricingDestinationSelect">Destination</label>
                    <select
                      id="pricingDestinationSelect"
                      value={selectedDestinationId}
                      onChange={(e) => setSelectedDestinationId(e.target.value)}
                    >
                      {destinations.map((destination) => (
                        <option key={destination.id} value={destination.id}>
                          {destination.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {products.length === 0 ? (
                    <p className="trip-pricing-empty">
                      Ajoutez d&apos;abord des produits dans l&apos;onglet 1.
                    </p>
                  ) : (
                    <div className="trip-pricing-price-grid">
                      <p className="trip-pricing-empty">Prix de vente pour cette destination :</p>
                      {products.map((product) => (
                        <div key={product.id} className="trip-pricing-price-row">
                          <label htmlFor={`price-${product.id}`}>
                            {product.name}
                            <small className="trip-pricing-usine-hint">
                              usine: {formatPrice(product.prix_usine ?? 0)}
                            </small>
                          </label>
                          <input
                            id={`price-${product.id}`}
                            type="number"
                            step="0.001"
                            min="0"
                            value={priceDrafts[product.id] ?? ""}
                            onChange={(e) =>
                              setPriceDrafts((prev) => ({
                                ...prev,
                                [product.id]: e.target.value,
                              }))
                            }
                            placeholder="0.000"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="trip-scheduler-form-actions">
                    <button
                      type="button"
                      className="trip-scheduler-save-btn"
                      onClick={handleSavePrices}
                      disabled={saving || products.length === 0}
                    >
                      Enregistrer les tarifs
                    </button>
                    <button
                      type="button"
                      className="trip-scheduler-cancel-btn"
                      onClick={() => handleDeleteDestination(selectedDestinationId)}
                      disabled={saving || !selectedDestinationId}
                    >
                      Supprimer la destination
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default TripPricingSettings;
